import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { isNotifiedError, useAuthFetchFromRestAPI } from '../../util/api';
import i18next from 'i18next';
import { TherapistDto } from '../../../../shared/dtos/therapist.dto';
import { Spinner } from 'react-bootstrap';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import MyAccountOverview from './Tabs/MyAccountOverview';
import TeleTherapy from './Tabs/TeleTherapy';
import AppointmentHistory from './Tabs/AppointmenHistory';
import TherapistSettings from './Tabs/TherapistSettings';
import { emitErrorNotification, emitSuccessNotification } from '../Notification';

type TabKey = 'overview' | 'trainingHistory' | 'appointmentHistory' | 'therapistSettings';

function notifySubmitError(error: unknown): void {
  if (isNotifiedError(error)) {
    return;
  }

  emitErrorNotification(i18next.t('myAccountPage.updateError'));
}

const MyAccountPage = (): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [activeKey, setActiveKey] = useState<TabKey>('overview');
  const [userProfile, setUserProfile] = useState<TherapistDto | null>(null);

  const fetchUserProfile: () => Promise<void> = useCallback(async () => {
    try {
      const result: TherapistDto = await authFetch('/api/v1/user-profile');
      setUserProfile(result);
    } catch (error) {
      console.error(error);
    }
  }, [authFetch]);

  useEffect(() => {
    void fetchUserProfile();
  }, [fetchUserProfile]);

  return (
    <div className="p-4 bg-white">
      <h1 className="fw-bold" style={{ fontSize: '4rem' }}>{i18next.t('myAccountPage.title')}</h1>

      {!userProfile ? (
        <div className="d-flex justify-content-center align-items-center flex-column gap-3">
          <Spinner animation="border" role="status" />
          <span>{i18next.t('generic.loading')}</span>
        </div>
      ) : (
        <Tabs
          id="myAccount"
          activeKey={activeKey}
          onSelect={(key) => setActiveKey((key as TabKey) ?? 'overview')}
          mountOnEnter
          unmountOnExit
        >
          <Tab eventKey="overview" title={i18next.t('myAccountPage.overviewTab')}>
            <MyAccountOverview
              userProfile={userProfile}
              onSubmitSuccess={(updated: TherapistDto) => {
                emitSuccessNotification(i18next.t('myAccountPage.updateSuccess'));
                setUserProfile(updated);
              }}
              onSubmitError={notifySubmitError}
            />
          </Tab>

          <Tab eventKey="therapistSettings" title={i18next.t('myAccountPage.therapistSettingsTab')}>
            <TherapistSettings userProfile={userProfile}
              onSubmitSuccess={(updated: TherapistDto) => {
                emitSuccessNotification(i18next.t('myAccountPage.updateSuccess'));
                setUserProfile(updated);
              }}
              onSubmitError={notifySubmitError}
            />
          </Tab>

          <Tab eventKey="trainingHistory" title={i18next.t('myAccountPage.trainingHistoryTab')}>
            <TeleTherapy />
          </Tab>

          <Tab eventKey="appointmentHistory" title={i18next.t('myAccountPage.appointmentHistoryTab')}>
            <AppointmentHistory />
          </Tab>
        </Tabs>
      )}
    </div>
  );
};

export default MyAccountPage;
